import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { BarChart3, Target, ClipboardList, Download, EyeOff } from 'lucide-react';
import ExportPreviewModal from './ExportPreviewModal';

const DashboardHeader = ({
  summary, reportingView, setReportingView,
  excludeExtraordinary, setExcludeExtraordinary,
  hasExtraordinary,
  activeTab, setActiveTab
}) => {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [showExportModal, setShowExportModal] = useState(false);

  const tabs = [
    { id: 'suivi', label: t('dashboard.tabs.tracking'), icon: ClipboardList },
    { id: 'resultats', label: t('dashboard.tabs.results'), icon: BarChart3 },
    { id: 'objectifs', label: t('dashboard.tabs.objectives'), icon: Target }
  ];

  return (
    <>
      {/* Header row */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('nav.dashboard')}
          </h1>
          <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {t('dashboard.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* FEAT-01d — Toggle masquer événements extraordinaires (Q2 reset session). */}
          {/* 🚨 RF1 : filtre la VUE de pilotage ; bilan GHG officiel + exports exhaustifs. */}
          {/* FEAT-01g : #1 couleur bleue (cohérence market/location) + #2 affichage
              conditionnel — visible uniquement si le FY a au moins 1 activité
              extraordinaire (Approche 1, détection FE sur activitiesData chargé). */}
          {hasExtraordinary && (
            <button
              data-testid="exclude-extraordinary-toggle"
              onClick={() => setExcludeExtraordinary(prev => !prev)}
              title={t('dashboard.hideExtraordinaryTooltip')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                excludeExtraordinary
                  ? 'bg-blue-500 text-white shadow-sm'
                  : isDark ? 'bg-slate-800 text-slate-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
            >
              <EyeOff className="w-3.5 h-3.5" />
              {t('dashboard.hideExtraordinary')}
            </button>
          )}
          {summary?.has_market_based && (
            <div data-testid="reporting-view-toggle" className={`flex items-center gap-1 p-0.5 rounded-lg text-xs font-medium ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
              <button
                onClick={() => setReportingView('market')}
                className={`px-3 py-1.5 rounded-md transition-all ${
                  reportingView === 'market'
                    ? 'bg-blue-500 text-white shadow-sm'
                    : isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                }`}
              >Market-based</button>
              <button
                onClick={() => setReportingView('location')}
                className={`px-3 py-1.5 rounded-md transition-all ${
                  reportingView === 'location'
                    ? 'bg-blue-500 text-white shadow-sm'
                    : isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                }`}
              >Location-based</button>
            </div>
          )}
          <button
            data-testid="export-report-btn"
            onClick={() => setShowExportModal(true)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
              isDark
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
            }`}
          >
            <Download className="w-4 h-4" />
            {t('dashboard.exportReport') || 'Exporter le rapport'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-2 p-1 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              // POLISH-1 — a 390px la somme des largeurs minimales des trois
              // onglets depasse la barre de 12px et etirait la page. Le
              // padding horizontal est resserre sous lg ; libelles intacts.
              className={`flex-1 flex items-center justify-center gap-2 px-4 max-lg:px-2 py-3 rounded-xl font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white shadow-lg'
                  : isDark
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }`}
            >
              <Icon className="w-5 h-5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Export Preview Modal */}
      <ExportPreviewModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        reportingView={reportingView}
      />
    </>
  );
};

export default DashboardHeader;
